import { ComponentProps, useEffect, useState } from 'react';
import { useAuth } from 'context/auth';
import { Prospect } from 'crono-fe-common/types/prospect';
import { ProspectSearchStrategyResponse } from 'crono-fe-common/types/prospectSearchStrategy';
import { checkIsProspectLead } from 'crono-fe-common/utils';
import PATH from 'crono-fe-common/constants/path';
import { useNavigate } from 'react-router-dom';
import { useGetProspectScore } from 'hooks/services/score-settings/useGetProspectScore';
import { useRefreshProspectScore } from 'hooks/services/score-settings/useRefreshProspectScore';
import { useLeadScoreExternalProps } from '../hooks/useLeadScoreExternalProps';
import { LeadScoreBadgeBase } from './LeadScoreBadgeBase';
import { useProspectScoreSettings } from '../hooks/useLeadScoreAccess';

type ProspectLeadScoreBadgeProps = {
  size?: 'xs' | 'sm' | 'md';
  iconSize?: 'xs' | 'sm' | 'md';
  prospect?: Prospect | ProspectSearchStrategyResponse | null;
  hidePopup?: boolean;
  forceOpen?: boolean;
  overlayProps?: ComponentProps<typeof LeadScoreBadgeBase>['overlayProps'];
};

export const ProspectLeadScoreBadge = ({
  size,
  iconSize,
  prospect,
  hidePopup = false,
  forceOpen = false,
  overlayProps,
}: ProspectLeadScoreBadgeProps) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
    }
  }, [forceOpen]);

  const prospectId = prospect?.objectId;
  const type =
    prospect && checkIsProspectLead(user, prospect) ? 'Lead' : 'Prospect';

  const { data: prospectScore, isInitialLoading: isLoadingProspectScore } =
    useGetProspectScore(prospectId, open);

  const {
    mutate: refreshProspectScore,
    isLoading: isLoadingRefreshProspectScore,
  } = useRefreshProspectScore();

  const handleRefresh = () => {
    if (prospectId) {
      refreshProspectScore(prospectId);
    }
  };

  const handleNavigateToSettings = () => {
    navigate({
      pathname: PATH.SETTINGS.LEAD_SCORE,
      search: new URLSearchParams({ tab: type }).toString(),
    });
  };

  const { defaultPropertiesByType } = useLeadScoreExternalProps(type);
  const defaultProperties = [
    ...(defaultPropertiesByType?.Account ?? []),
    ...(defaultPropertiesByType.Prospect ?? []),
  ];

  const { getProspectOrLeadSettings } = useProspectScoreSettings(type);
  const scoreSettings = getProspectOrLeadSettings(prospect);

  const scoreDetails = prospectScore?.data?.data;
  const scoreShort = prospect?.score;

  useEffect(() => {
    if (hidePopup) {
      setOpen(false);
    }
  }, [hidePopup]);

  if (!prospect) {
    return null;
  }

  return (
    <LeadScoreBadgeBase
      type={type}
      size={size}
      iconSize={iconSize}
      scoreSettings={scoreSettings}
      scoreDetails={scoreDetails}
      scoreShort={scoreShort}
      defaultProperties={defaultProperties}
      loading={isLoadingProspectScore}
      refreshLoading={isLoadingRefreshProspectScore}
      onRefresh={handleRefresh}
      onSettings={handleNavigateToSettings}
      overlayProps={overlayProps}
      open={open}
      onOpenChange={setOpen}
    />
  );
};
